(() => {
  'use strict';

  const config = window.MoonMan || {};
  const routes = config.routes || {};
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const qs = (selector, context = document) => context.querySelector(selector);

  const escapeHTML = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const cartEndpoint = (route, suffix) => {
    const value = route || '';
    return value.endsWith(suffix) ? value : `${value}${suffix}`;
  };

  const resizedImageUrl = (url, width = 180) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const formatMoney = (cents, format = config.moneyFormat || '${{amount}}') => {
    const value = Number(cents || 0);
    const placeholderRegex = /\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/;
    const match = format.match(placeholderRegex);
    if (!match) return `$${(value / 100).toFixed(2)}`;

    const addDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };

    let formatted = '';
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = addDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = addDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = addDelimiters(value, 0, '.', ',');
        break;
      default:
        formatted = addDelimiters(value, 2);
    }
    return format.replace(placeholderRegex, formatted);
  };

  const showToast = (message, timeout = 2800) => {
    const toast = qs('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), timeout);
  };

  const openDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.body.classList.add('no-scroll');
    const focusTarget = qs('[autofocus], input, button, a[href]', dialog);
    window.setTimeout(() => focusTarget?.focus(), 30);
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
    if (!qs('dialog[open]')) document.body.classList.remove('no-scroll');
  };

  const initializeDialogs = (context = document) => {
    qsa('[data-dialog-open]', context).forEach((trigger) => {
      if (trigger.dataset.dialogBound) return;
      trigger.dataset.dialogBound = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const sourceDialog = trigger.closest('dialog');
        const targetDialog = document.getElementById(trigger.dataset.dialogOpen);
        if (sourceDialog && sourceDialog !== targetDialog) closeDialog(sourceDialog);
        openDialog(targetDialog);
      });
    });

    qsa('[data-dialog-close]', context).forEach((trigger) => {
      if (trigger.dataset.dialogBound) return;
      trigger.dataset.dialogBound = 'true';
      trigger.addEventListener('click', () => closeDialog(trigger.closest('dialog')));
    });

    qsa('dialog', context).forEach((dialog) => {
      if (dialog.dataset.backdropBound) return;
      dialog.dataset.backdropBound = 'true';
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });
      dialog.addEventListener('close', () => {
        if (!qs('dialog[open]')) document.body.classList.remove('no-scroll');
      });
    });
  };

  const initializeHeader = () => {
    const header = qs('[data-site-header]');
    if (!header || header.dataset.scrollBound) return;
    header.dataset.scrollBound = 'true';
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initializeQuantitySelectors = (context = document) => {
    qsa('[data-quantity-button]', context).forEach((button) => {
      if (button.dataset.quantityBound) return;
      button.dataset.quantityBound = 'true';
      button.addEventListener('click', () => {
        const wrapper = button.closest('[data-quantity-selector]');
        const input = qs('input[type="number"]', wrapper);
        if (!input) return;
        const step = Number(input.step || 1);
        const minimum = Number(input.min || 0);
        const maximum = input.max ? Number(input.max) : Infinity;
        const direction = button.dataset.quantityButton === 'plus' ? 1 : -1;
        input.value = Math.max(minimum, Math.min(maximum, Number(input.value || 0) + direction * step));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const updateShippingProgress = (cart) => {
    qsa('[data-shipping-threshold]').forEach((wrapper) => {
      const threshold = Number(wrapper.dataset.shippingThreshold || config.freeShippingThreshold || 0);
      const remaining = Math.max(threshold - cart.total_price, 0);
      const progress = threshold > 0 ? Math.min((cart.total_price / threshold) * 100, 100) : 100;
      const message = qs('[data-shipping-message]', wrapper);
      const bar = qs('[data-shipping-bar]', wrapper);
      if (message) {
        message.textContent = remaining > 0
          ? `You're ${formatMoney(remaining)} away from free shipping.`
          : 'You unlocked free shipping.';
      }
      if (bar) bar.style.width = `${progress}%`;
    });
  };

  const renderCartDrawer = (cart, shouldOpen = false) => {
    qsa('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.hidden = cart.item_count === 0;
    });

    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    const itemsContainer = qs('[data-cart-drawer-items]', drawer);
    const subtotal = qs('[data-cart-drawer-subtotal]', drawer);
    const emptyState = qs('[data-cart-empty]', drawer);
    const footer = qs('[data-cart-drawer-footer]', drawer);

    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    updateShippingProgress(cart);

    if (cart.item_count === 0) {
      if (itemsContainer) itemsContainer.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      if (footer) footer.hidden = true;
    } else {
      if (emptyState) emptyState.hidden = true;
      if (footer) footer.hidden = false;
      if (itemsContainer) {
        itemsContainer.innerHTML = cart.items.map((item, index) => {
          const image = item.featured_image?.url || item.image || '';
          const variant = item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="drawer-item__variant">${escapeHTML(item.variant_title)}</div>`
            : '';
          return `
            <div class="drawer-item" data-drawer-line="${index + 1}">
              <a href="${escapeHTML(item.url)}">
                ${image ? `<img class="drawer-item__image" src="${escapeHTML(resizedImageUrl(image, 180))}" alt="${escapeHTML(item.product_title)}" width="90" height="100">` : ''}
              </a>
              <div>
                <a class="drawer-item__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
                ${variant}
                <div class="drawer-item__qty">
                  <button type="button" data-drawer-quantity="minus" aria-label="Decrease quantity">−</button>
                  <input type="number" value="${item.quantity}" min="0" aria-label="Quantity for ${escapeHTML(item.product_title)}" data-drawer-quantity-input>
                  <button type="button" data-drawer-quantity="plus" aria-label="Increase quantity">+</button>
                </div>
              </div>
              <div class="drawer-item__price">${formatMoney(item.final_line_price)}</div>
            </div>`;
        }).join('');
      }
    }

    if (shouldOpen) openDialog(drawer);
  };

  const fetchCart = async (shouldOpen = false) => {
    try {
      const response = await fetch(cartEndpoint(routes.cart, '.js'), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to load cart');
      const cart = await response.json();
      renderCartDrawer(cart, shouldOpen);
      return cart;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const changeCartLine = async (line, quantity) => {
    try {
      const response = await fetch(cartEndpoint(routes.cartChange, '.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update cart');
      renderCartDrawer(cart);
    } catch (error) {
      showToast(error.message || 'Unable to update cart');
      await fetchCart();
    }
  };

  const initializeCartDrawer = () => {
    if (!config.cartDrawer) return;

    qsa('[data-cart-open]').forEach((trigger) => {
      if (trigger.dataset.cartBound) return;
      trigger.dataset.cartBound = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        fetchCart(true);
      });
    });

    const drawer = qs('[data-cart-drawer]');
    if (!drawer || drawer.dataset.cartEventsBound) return;
    drawer.dataset.cartEventsBound = 'true';

    drawer.addEventListener('click', (event) => {
      const button = event.target.closest('[data-drawer-quantity]');
      if (!button) return;
      const item = button.closest('[data-drawer-line]');
      const input = qs('[data-drawer-quantity-input]', item);
      const line = Number(item.dataset.drawerLine);
      const direction = button.dataset.drawerQuantity === 'plus' ? 1 : -1;
      changeCartLine(line, Math.max(0, Number(input.value || 0) + direction));
    });

    drawer.addEventListener('change', (event) => {
      const input = event.target.closest('[data-drawer-quantity-input]');
      if (!input) return;
      const item = input.closest('[data-drawer-line]');
      changeCartLine(Number(item.dataset.drawerLine), Math.max(0, Number(input.value || 0)));
    });
  };

  const initializeQuickAdd = (context = document) => {
    qsa('[data-quick-add]', context).forEach((button) => {
      if (button.dataset.quickAddBound) return;
      button.dataset.quickAddBound = 'true';

      button.addEventListener('click', async () => {
        const variantId = Number(button.dataset.variantId);
        const status = qs('[data-quick-add-status]', button.closest('.quick-add-form'));
        const label = qs('[data-quick-add-text]', button) || button;
        const originalLabel = label.textContent;
        if (!variantId || button.disabled) return;

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        label.textContent = 'Adding…';
        if (status) {
          status.textContent = '';
          status.classList.remove('is-error', 'is-success');
        }

        try {
          const response = await fetch(cartEndpoint(routes.cartAdd, '.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'This item could not be added.');

          label.textContent = 'Added';
          if (status) {
            status.textContent = 'Added to your cart.';
            status.classList.add('is-success');
          }
          await fetchCart(Boolean(config.cartDrawer));
          showToast(`${button.dataset.productTitle || 'Item'} added to cart`);
          window.setTimeout(() => { label.textContent = originalLabel; }, 1800);
        } catch (error) {
          label.textContent = originalLabel;
          if (status) {
            status.textContent = error.message;
            status.classList.add('is-error');
          }
          showToast(error.message || 'Unable to add item');
        } finally {
          button.removeAttribute('aria-busy');
          button.disabled = originalLabel.trim().toLowerCase() === 'sold out';
        }
      });
    });
  };

  const initializeProductForms = (context = document) => {
    qsa('form[data-product-form]', context).forEach((form) => {
      if (form.dataset.ajaxBound) return;
      form.dataset.ajaxBound = 'true';

      form.addEventListener('submit', async (event) => {
        if (!config.cartDrawer || event.submitter?.name === 'checkout') return;
        event.preventDefault();

        const submitButton = event.submitter || qs('[type="submit"]', form);
        const status = qs('[data-form-status]', form);
        submitButton?.setAttribute('aria-busy', 'true');
        submitButton?.setAttribute('disabled', '');

        try {
          const response = await fetch(cartEndpoint(routes.cartAdd, '.js'), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: new FormData(form)
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'This item could not be added.');
          if (status) {
            status.textContent = 'Added to your cart.';
            status.classList.remove('is-error');
            status.classList.add('is-success');
          }
          await fetchCart(true);
          showToast('Added to cart');
        } catch (error) {
          if (status) {
            status.textContent = error.message;
            status.classList.remove('is-success');
            status.classList.add('is-error');
          }
          showToast(error.message || 'Unable to add item');
        } finally {
          submitButton?.removeAttribute('aria-busy');
          const productSection = form.closest('[data-product-section]');
          const currentVariant = productSection?.moonManVariant;
          if (!currentVariant || currentVariant.available) submitButton?.removeAttribute('disabled');
        }
      });
    });
  };

  const activateProductMedia = (section, mediaId) => {
    if (!mediaId) return;
    qsa('[data-product-media]', section).forEach((media) => {
      media.classList.toggle('is-active', String(media.dataset.productMedia) === String(mediaId));
    });
    qsa('[data-product-thumb]', section).forEach((thumb) => {
      const active = String(thumb.dataset.productThumb) === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
  };

  const initializeProductSections = (context = document) => {
    qsa('[data-product-section]', context).forEach((section) => {
      if (section.dataset.productBound) return;
      section.dataset.productBound = 'true';

      const jsonNode = qs('[data-product-json]', section);
      let productData = null;
      try {
        productData = JSON.parse(jsonNode?.textContent || '{}');
      } catch (error) {
        console.error('Invalid product JSON', error);
      }
      if (!productData?.variants) return;

      const selects = qsa('[data-option-select]', section);
      const variantInput = qs('input[name="id"]', section);
      const addButtons = qsa('[data-add-to-cart]', section);
      const stickyButtons = qsa('[data-sticky-add]', section);
      const priceNodes = qsa('[data-product-price]', section);
      const compareNodes = qsa('[data-compare-price]', section);
      const inventoryNode = qs('[data-product-inventory]', section);
      const skuNode = qs('[data-product-sku]', section);
      const stickyPrice = qs('[data-sticky-price]', section);
      const lowInventoryThreshold = Number(inventoryNode?.dataset.lowInventoryThreshold || 5);

      const updateVariant = () => {
        const selectedOptions = selects.map((select) => select.value);
        selects.forEach((select) => {
          const selectedValue = select.closest('.product-option')?.querySelector('label span');
          if (selectedValue) selectedValue.textContent = select.value;
        });

        let variant = null;
        if (selects.length === 0) {
          variant = productData.variants.find((candidate) => String(candidate.id) === String(variantInput?.value))
            || productData.variants.find((candidate) => candidate.available)
            || productData.variants[0];
        } else {
          variant = productData.variants.find((candidate) =>
            candidate.options.every((option, index) => String(option) === String(selectedOptions[index]))
          );
        }

        section.moonManVariant = variant || null;
        if (!variant) {
          addButtons.forEach((button) => {
            button.disabled = true;
            const label = qs('[data-add-text]', button) || button;
            label.textContent = 'Unavailable';
          });
          stickyButtons.forEach((button) => {
            button.disabled = true;
            const label = qs('[data-sticky-add-text]', button) || button;
            label.textContent = 'Unavailable';
          });
          return;
        }

        if (variantInput) variantInput.value = variant.id;
        priceNodes.forEach((node) => { node.textContent = formatMoney(variant.price); });
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

        compareNodes.forEach((node) => {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            node.textContent = formatMoney(variant.compare_at_price);
            node.hidden = false;
          } else {
            node.hidden = true;
          }
        });

        addButtons.forEach((button) => {
          button.disabled = !variant.available;
          const label = qs('[data-add-text]', button) || button;
          label.textContent = variant.available ? 'Add to cart' : 'Sold out';
        });
        stickyButtons.forEach((button) => {
          button.disabled = !variant.available;
          const label = qs('[data-sticky-add-text]', button) || button;
          label.textContent = variant.available ? 'Add to cart' : 'Sold out';
        });

        if (inventoryNode) {
          inventoryNode.classList.remove('is-low', 'is-out');
          if (!variant.available) {
            inventoryNode.classList.add('is-out');
            inventoryNode.lastElementChild.textContent = 'Sold out';
          } else if (
            variant.inventory_management
            && variant.inventory_quantity > 0
            && variant.inventory_quantity <= lowInventoryThreshold
          ) {
            inventoryNode.classList.add('is-low');
            inventoryNode.lastElementChild.textContent = `Only ${variant.inventory_quantity} left`;
          } else {
            inventoryNode.lastElementChild.textContent = 'Ready to order';
          }
        }

        if (skuNode) {
          skuNode.textContent = variant.sku || '';
          skuNode.closest('[data-sku-wrapper]')?.toggleAttribute('hidden', !variant.sku);
        }

        if (variant.featured_media?.id) activateProductMedia(section, variant.featured_media.id);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      selects.forEach((select) => select.addEventListener('change', updateVariant));
      updateVariant();

      qsa('[data-product-thumb]', section).forEach((thumb) => {
        thumb.addEventListener('click', () => activateProductMedia(section, thumb.dataset.productThumb));
      });

      qsa('[data-media-zoom]', section).forEach((button) => {
        button.addEventListener('click', () => {
          const dialog = document.getElementById(button.dataset.mediaZoom);
          const image = qs('[data-zoom-image]', dialog);
          if (image) {
            image.src = button.dataset.zoomSrc;
            image.alt = button.dataset.zoomAlt || '';
          }
          openDialog(dialog);
        });
      });

      qsa('[data-sticky-add]', section).forEach((button) => {
        button.addEventListener('click', () => qs('[data-add-to-cart]', section)?.click());
      });
    });
  };

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    textArea.remove();
  };

  const initializeSharing = (context = document) => {
    qsa('[data-share]', context).forEach((button) => {
      if (button.dataset.shareBound) return;
      button.dataset.shareBound = 'true';
      button.addEventListener('click', async () => {
        const title = button.dataset.shareTitle || document.title;
        const url = button.dataset.shareUrl || window.location.href;
        const text = config.shareHashtag ? `${title} ${config.shareHashtag}` : title;
        try {
          if (navigator.share) {
            await navigator.share({ title, text, url });
          } else {
            await copyText(url);
            const group = button.closest('[data-share-group]');
            const status = qs('[data-share-status]', group);
            if (status) {
              status.textContent = 'Link copied.';
              window.setTimeout(() => { status.textContent = ''; }, 2200);
            } else {
              showToast('Link copied');
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') showToast('Unable to share this page');
        }
      });
    });
  };

  const initializeCountdowns = (context = document) => {
    qsa('[data-countdown]', context).forEach((countdown) => {
      if (countdown.dataset.countdownBound) return;
      countdown.dataset.countdownBound = 'true';
      const end = Date.parse(countdown.dataset.countdown);
      if (!Number.isFinite(end)) return;

      const update = () => {
        const remaining = end - Date.now();
        if (remaining <= 0) {
          qsa('[data-countdown-number]', countdown).forEach((node) => { node.textContent = '00'; });
          const expired = qs('[data-countdown-expired]', countdown);
          if (expired) expired.hidden = false;
          window.clearInterval(countdown.timer);
          return;
        }
        const totalSeconds = Math.floor(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const values = { days, hours, minutes, seconds };
        Object.entries(values).forEach(([key, value]) => {
          const node = qs(`[data-countdown-number="${key}"]`, countdown);
          if (node) node.textContent = String(value).padStart(2, '0');
        });
      };
      update();
      countdown.timer = window.setInterval(update, 1000);
    });
  };

  const initializeReveal = (context = document) => {
    const elements = qsa('.reveal:not(.is-visible)', context);
    if (!elements.length) return;
    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    elements.forEach((element) => observer.observe(element));
  };

  const initializeFacets = (context = document) => {
    qsa('[data-filter-toggle]', context).forEach((button) => {
      if (button.dataset.filterBound) return;
      button.dataset.filterBound = 'true';
      button.addEventListener('click', () => {
        const facets = qs('[data-facets]', button.closest('[data-collection-section]') || document);
        facets?.classList.toggle('is-open');
      });
    });
    qsa('[data-sort-select]', context).forEach((select) => {
      if (select.dataset.sortBound) return;
      select.dataset.sortBound = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', this.debounce(() => this.search(), 250));
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.results.innerHTML = '';
          this.input.setAttribute('aria-expanded', 'false');
        }
      });
    }

    debounce(callback, wait) {
      let timeout;
      return (...args) => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => callback.apply(this, args), wait);
      };
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }

      this.controller?.abort();
      this.controller = new AbortController();
      const url = `${routes.searchSuggest}?q=${encodeURIComponent(query)}&resources[type]=product,page,article&resources[limit]=8&section_id=predictive-search`;

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Search failed');
        const text = await response.text();
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const resultContent = qs('#shopify-section-predictive-search', documentFragment) || documentFragment.body;
        this.results.innerHTML = resultContent?.innerHTML || '';
        this.input.setAttribute('aria-expanded', this.results.innerHTML ? 'true' : 'false');
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized || !this.dataset.url) return;
      this.dataset.initialized = 'true';
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const source = html.querySelector('product-recommendations');
          if (source?.innerHTML.trim()) this.innerHTML = source.innerHTML;
          initializeTheme(this);
        })
        .catch((error) => console.error(error));
    }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  const initializeTheme = (context = document) => {
    initializeDialogs(context);
    initializeHeader();
    initializeQuantitySelectors(context);
    initializeCartDrawer();
    initializeQuickAdd(context);
    initializeProductForms(context);
    initializeProductSections(context);
    initializeSharing(context);
    initializeCountdowns(context);
    initializeReveal(context);
    initializeFacets(context);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initializeTheme();
    if (config.cartDrawer) fetchCart(false);
  });

  document.addEventListener('shopify:section:load', (event) => initializeTheme(event.target));
})();

/* Select the matching color when a customer clicks a product mockup. */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const thumb = event.target.closest('[data-product-thumb]');
  if (!thumb) return;

  const section = thumb.closest('[data-product-section]');
  if (!section) return;

  const selects = Array.from(section.querySelectorAll('[data-option-select]'));
  const colorSelect = selects.find((select) => {
    const label = section.querySelector(`label[for="${select.id}"]`);
    return label?.textContent.trim().toLowerCase().startsWith('color');
  });
  if (!colorSelect) return;

  const optionIndex = Number(colorSelect.dataset.optionIndex || 0);
  const mediaId = String(thumb.dataset.productThumb || '');
  let color = '';

  try {
    const productData = JSON.parse(section.querySelector('[data-product-json]')?.textContent || '{}');
    const matchingVariant = productData.variants?.find(
      (variant) => String(variant.featured_media?.id || '') === mediaId
    );
    color = matchingVariant?.options?.[optionIndex] || '';
  } catch {
    // Extra mockups can still be matched from their image descriptions.
  }

  if (!color) {
    const imageText = `${thumb.getAttribute('aria-label') || ''} ${thumb.querySelector('img')?.alt || ''}`.toLowerCase();
    color = Array.from(colorSelect.options)
      .map((option) => option.value)
      .sort((a, b) => b.length - a.length)
      .find((value) => imageText.includes(value.toLowerCase())) || '';
  }

  if (!color || colorSelect.value === color) return;

  colorSelect.value = color;
  colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
}, true);
